import React, { useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

/**
 * Wraps report content with a hospital header and provides Export to PDF.
 * Add class "no-print" to elements that should be hidden in the PDF (filters, buttons).
 */
const ReportPdfLayout = ({ reportTitle, fileName = "report.pdf", children, disabled = false }) => {
  const { t } = useTranslation();
  const pdfRef = useRef(null);

  const handleExportPdf = async () => {
    if (!pdfRef.current) return;

    const el = pdfRef.current;
    const noPrint = el.querySelectorAll(".no-print");
    noPrint.forEach((n) => (n.style.display = "none"));

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });
      noPrint.forEach((n) => (n.style.display = ""));

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      if (pdfHeight > pdf.internal.pageSize.getHeight() - 20) {
        const pageHeight = pdf.internal.pageSize.getHeight() - 20;
        let heightLeft = pdfHeight;
        let position = 10;
        let page = 1;
        pdf.addImage(imgData, "PNG", 10, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
          pdf.addPage();
          position = heightLeft - pdfHeight;
          pdf.addImage(imgData, "PNG", 10, position, pdfWidth, pdfHeight);
          heightLeft -= pageHeight;
          page++;
        }
      } else {
        pdf.addImage(imgData, "PNG", 10, 10, pdfWidth, pdfHeight);
      }

      pdf.save(fileName);
    } catch (err) {
      console.error("PDF export failed", err);
      noPrint.forEach((n) => (n.style.display = ""));
    }
  };

  return (
    <>
      <div ref={pdfRef} style={{ padding: 8 }}>
        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {t("print.turkishClinic")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("print.address")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("print.phoneNumber")}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1.5 }} fontWeight={600}>
            {reportTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("reports.generatedOn")}: {new Date().toLocaleString()}
          </Typography>
        </Box>
        {children}
      </div>
      <Box className="no-print" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleExportPdf}
          disabled={disabled}
        >
          {t("reports.exportPdf")}
        </Button>
      </Box>
    </>
  );
};

export default ReportPdfLayout;
